/**
 * NARA Care — Ilustraciones SVG decorativas para el branding médico.
 */
import React from 'react';
import { Box, keyframes } from '@mui/material';

// ============================================================================
// Helper: genera el path SVG de un cardiograma con N ciclos de PQRST.
// ============================================================================
function generarPathCardiograma(viewWidth, viewHeight, cicloAncho, baseline, escala = 1) {
  // Cada ciclo PQRST mide `cicloAncho` px y representa un latido completo.
  // Estructura del latido: línea base → P → línea base → Q → R (pico grande) → S (valle) → línea base → T → línea base
  const cycles = Math.ceil(viewWidth / cicloAncho) + 1;
  let path = `M0 ${baseline}`;

  // Offsets X relativos al inicio del ciclo (suman cicloAncho)
  // Diseño de un latido elegante: descansa la mayor parte del ciclo
  const segments = [
    { x: 0,   y: baseline },              // línea base inicial
    { x: 90,  y: baseline },              // descanso largo
    { x: 96,  y: baseline - 5 * escala }, // P wave start
    { x: 102, y: baseline - 8 * escala }, // P peak
    { x: 110, y: baseline },              // back to baseline
    { x: 116, y: baseline },              // pequeño descanso
    { x: 120, y: baseline + 4 * escala }, // Q (pequeña caída)
    { x: 128, y: baseline - 55 * escala }, // R (PICO grande arriba)
    { x: 136, y: baseline + 28 * escala }, // S (valle pronunciado abajo)
    { x: 142, y: baseline },              // back to baseline
    { x: 152, y: baseline - 6 * escala }, // T wave start
    { x: 160, y: baseline - 9 * escala }, // T peak
    { x: 170, y: baseline },              // back to baseline
  ];

  for (let i = 0; i < cycles; i++) {
    const xOffset = i * cicloAncho;
    for (const seg of segments) {
      const x = xOffset + seg.x;
      if (x > viewWidth + cicloAncho) break;
      path += ` L${x.toFixed(1)} ${seg.y.toFixed(1)}`;
    }
  }
  path += ` L${viewWidth} ${baseline}`;
  return path;
}

// Animación sutil: pulso de oscilación de opacidad (efecto "monitor en vivo")
const pulseGlow = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.55; }
`;

/**
 * Cardiograma decorativo para la columna izquierda del login.
 * Dos líneas: una prominente dorada con sutil glow animado, y una secundaria blanca tenue.
 */
export const LoginPulse = () => {
  const W = 1440;
  const H = 200;
  // Línea principal (dorada): cardiograma grande
  const pathPrincipal = generarPathCardiograma(W, H, 280, 130, 1);
  // Línea secundaria (blanca tenue): cardiograma más pequeño y desfasado
  const pathSecundario = generarPathCardiograma(W, H, 280, 100, 0.6);

  return (
    <Box sx={{
      position: 'absolute', bottom: 0, left: 0, right: 0,
      height: H, pointerEvents: 'none', zIndex: 0, overflow: 'hidden',
    }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%' }}
      >
        {/* Línea base sutil (referencia del monitor) */}
        <line
          x1="0" y1="130" x2={W} y2="130"
          stroke="#fff" strokeWidth="0.5" opacity="0.06"
          strokeDasharray="3 6"
        />

        {/* Cardiograma secundario (blanco, fondo) */}
        <path
          d={pathSecundario}
          stroke="#fff" strokeWidth="1" fill="none"
          opacity="0.1"
          strokeLinejoin="round" strokeLinecap="round"
        />

        {/* Cardiograma principal (dorado, prominente) */}
        <Box
          component="path"
          d={pathPrincipal}
          stroke="#fbbf24" strokeWidth="1.6" fill="none"
          opacity="0.4"
          strokeLinejoin="round" strokeLinecap="round"
          sx={{
            animation: `${pulseGlow} 3.5s ease-in-out infinite`,
            filter: 'drop-shadow(0 0 6px rgba(251, 191, 36, 0.4))',
          }}
        />
      </svg>
    </Box>
  );
};

// Alias retrocompatible (Login.jsx ya importa LoginWaves; lo dejamos apuntando al cardiograma)
export const LoginWaves = LoginPulse;

/**
 * Spinner médico — pulso de cardiograma estilizado animado.
 */
export const MedicalLoader = ({ size = 48, color = '#1e3a5f' }) => (
  <Box sx={{ width: size, height: size, mx: 'auto' }}>
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2 24 L12 24 L16 14 L22 34 L28 18 L34 24 L46 24"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity="0.85"
      >
        <animate
          attributeName="stroke-dashoffset"
          dur="1.4s"
          repeatCount="indefinite"
          values="100;0"
        />
        <animate
          attributeName="stroke-dasharray"
          dur="1.4s"
          repeatCount="indefinite"
          values="0 100;100 0"
        />
      </path>
    </svg>
  </Box>
);
